import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { AuthShell } from "@/components/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function Register() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/";
  const [step, setStep] = useState("age"); // age | blocked | form
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [privacy, setPrivacy] = useState(false);
  const [terms, setTerms] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);

  const canSubmit = email && password && confirm && privacy && terms && !loading;

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Le password non coincidono.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/register", {
        email,
        password,
        confirm_password: confirm,
        age_confirmed_14: true,
        privacy_accepted: privacy,
        terms_accepted: terms,
        next_path: next,
      });
      toast.success("Account creato. Controlla la tua e-mail.");
      navigate(
        `/verify-email?email=${encodeURIComponent(email)}&next=${encodeURIComponent(next)}`
      );
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (step === "age") {
    return (
      <AuthShell
        testid="agegate-card"
        title="DICHIARI DI AVERE COMPIUTO 14 ANNI?"
      >
        <div className="flex flex-col gap-3">
          <Button
            data-testid="agegate-yes-btn"
            className="w-full bg-[#e8836b] hover:bg-[#d76f57] text-white rounded-full py-6 text-sm font-semibold"
            onClick={() => setStep("form")}
          >
            SÌ, DICHIARO DI AVERE COMPIUTO IL 14° ANNO DI ETÀ
          </Button>
          <Button
            data-testid="agegate-no-btn"
            variant="outline"
            className="w-full rounded-full py-6 text-sm font-semibold border-[#e0d4c4]"
            onClick={() => setStep("blocked")}
          >
            NO
          </Button>
        </div>
      </AuthShell>
    );
  }

  if (step === "blocked") {
    return (
      <AuthShell
        testid="agegate-blocked-card"
        title="Registrazione non disponibile"
        subtitle="Per creare un account devi avere compiuto almeno 14 anni."
      >
        <Button
          data-testid="agegate-back-btn"
          variant="outline"
          className="w-full rounded-full py-5 border-[#e0d4c4]"
          onClick={() => setStep("age")}
        >
          Indietro
        </Button>
      </AuthShell>
    );
  }

  return (
    <AuthShell testid="register-card" title="Crea il tuo account">
      <form onSubmit={submit} className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="email">E-mail</Label>
          <Input
            id="email"
            data-testid="register-email-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="password">Password</Label>
          <div className="relative">
            <Input
              id="password"
              data-testid="register-password-input"
              type={showPw ? "text" : "password"}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="pr-10"
            />
            <button
              type="button"
              data-testid="register-password-toggle"
              onClick={() => setShowPw((v) => !v)}
              aria-label={showPw ? "Nascondi password" : "Mostra password"}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9a8f83] hover:text-[#4a433c]"
            >
              {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="confirm">Conferma password</Label>
          <div className="relative">
            <Input
              id="confirm"
              data-testid="register-confirm-input"
              type={showConfirm ? "text" : "password"}
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="pr-10"
            />
            <button
              type="button"
              data-testid="register-confirm-toggle"
              onClick={() => setShowConfirm((v) => !v)}
              aria-label={showConfirm ? "Nascondi password" : "Mostra password"}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9a8f83] hover:text-[#4a433c]"
            >
              {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>

        <label className="flex items-start gap-2 text-sm text-[#4a433c]">
          <Checkbox
            data-testid="register-privacy-checkbox"
            checked={privacy}
            onCheckedChange={(v) => setPrivacy(!!v)}
            className="mt-0.5"
          />
          <span>Dichiaro di aver letto l’
            <Link
              data-testid="register-privacy-link"
              to="/privacy"
              target="_blank"
              rel="noreferrer"
              className="text-[#c85f45] font-semibold underline"
            >
              Informativa Privacy
            </Link>.
          </span>
        </label>
        <label className="flex items-start gap-2 text-sm text-[#4a433c]">
          <Checkbox
            data-testid="register-terms-checkbox"
            checked={terms}
            onCheckedChange={(v) => setTerms(!!v)}
            className="mt-0.5"
          />
          <span>Accetto i
            <Link
              data-testid="register-terms-link"
              to="/terms"
              target="_blank"
              rel="noreferrer"
              className="text-[#c85f45] font-semibold underline"
            >
              {" "}Termini e Condizioni di utilizzo
            </Link>.
          </span>
        </label>
        <p className="text-xs text-[#9a8f83]">
          I testi definitivi di Privacy e Termini saranno forniti dal committente.
        </p>

        <Button
          type="submit"
          data-testid="register-submit-btn"
          disabled={!canSubmit}
          className="w-full bg-[#e8836b] hover:bg-[#d76f57] text-white rounded-full py-6 text-sm font-semibold disabled:opacity-50"
        >
          {loading ? "Creazione in corso…" : "CREA IL MIO ACCOUNT"}
        </Button>
      </form>
      <p className="text-sm text-center text-[#7a6f64] mt-4">
        Hai già un account?{" "}
        <Link data-testid="register-to-login-link" to="/login" className="text-[#c85f45] font-semibold">
          Accedi
        </Link>
      </p>
    </AuthShell>
  );
}
